// LetterTile — a project's neutral mark: its first letter in a small outlined
// tile, pure (pen "Shell · Rail" and "Shell · Command bar", 29 Oct). Projects
// carry no colour (owner, 27 Oct), so the letter is the only mark.
//
// Two sizes, as drawn: "md" (20, the rail) and "sm" (12, a command bar row).
// Decorative: the caller names the project (tooltip, row title), so the tile
// is aria-hidden. A name is plain text; the tile shows one character of it.

export { initialOf, LetterTile };

import type { ReactNode } from "react";
import "./LetterTile.css";

type LetterTileSize = "sm" | "md";

// The first letter or digit, upper-cased; a name with neither shows its first
// visible character; an empty or blank name shows nothing (an empty tile).
// Code points, not UTF-16 units, so an astral character is never split; a
// letter whose capital is two ("ß" → "SS") stays as it is, one character.
const initialOf = (name: string, locale: string): string => {
    const chars = Array.from(name);
    const first =
        chars.find((c) => /[\p{L}\p{N}]/u.test(c)) ??
        chars.find((c) => /\S/u.test(c)) ??
        "";
    const upper = first.toLocaleUpperCase(locale);
    return Array.from(upper).length === 1 ? upper : first;
};

const LetterTile = ({
    name,
    locale,
    size,
}: {
    readonly name: string;
    readonly locale: string;
    readonly size: LetterTileSize;
}): ReactNode => (
    <span className="bilda-letter-tile" data-size={size} aria-hidden="true">
        {initialOf(name, locale)}
    </span>
);
